import axios from 'axios'
import { getSelectedSeason } from '../context/SeasonContext'

// Instance axios unique de l'app (ARCHI-001).
// Ce fichier fusionne :
//  - la config historique de services/api.js (baseURL, token, season, 401)
//  - l'intercepteur d'erreur global BUG-011 : remonte aux composants un
//    err.message lisible (detail FastAPI) au lieu du "Request failed with
//    status code 4xx" brut d'axios.
// services/api.js est conservé comme simple ré-export de ce module pour ne
// pas casser les imports existants.

const API_URL = import.meta.env.VITE_API_URL || 'https://backend-pued.onrender.com/api'

// Endpoints qui doivent recevoir automatiquement le paramètre ?season=
// Tous les endpoints qui retournent des données dépendantes de la saison.
// On utilise des regex pour matcher les patterns (ex: /matches/{id}/events/).
const SEASONAL_ENDPOINTS = [
  /^\/matches\/?$/,                   // GET /matches/  liste des matchs
  /^\/matches\/quota\/?$/,            // GET /matches/quota
  /^\/players\/[^/]+\/stats\/?$/,     // GET /players/{id}/stats/
  /^\/stats\/?/,                      // GET /stats/...
  /^\/club\/stats\/?/,                // GET /club/stats/...
]

// Endpoints à NE JAMAIS saisonnaliser même si match du pattern
// (paiement, auth, settings, etc.)
const EXCLUDED_PATHS = [
  '/auth/',
  '/subscription/',
  '/account/',
  '/club/me',
  '/seances/',
]

function shouldAddSeason(url) {
  if (!url) return false
  // Strip query string
  const path = url.split('?')[0]

  // Exclusions explicites
  for (const excl of EXCLUDED_PATHS) {
    if (path.includes(excl)) return false
  }

  // Match contre la liste blanche
  for (const pattern of SEASONAL_ENDPOINTS) {
    if (pattern.test(path)) return true
  }
  return false
}

// TIMEOUT — 60 s, alors qu'axios n'en pose AUCUN par defaut.
//
// Sans timeout, une requete perdue pend indefiniment et l'ecran reste bloque.
// Mais le reglage se choisit par le HAUT, pas par le bas · c'est un timeout
// TROP COURT qui a produit P1-CONFIRM-PLAN-IDEMPOTENT-001. Le backend dort sur
// Render, un cold start rend la main apres que le serveur a fait le travail,
// le `catch` de l'appelant affiche une erreur et rearme son bouton — et
// l'utilisateur reessaie une operation deja executee. Sur `confirm-plan`,
// cela creait un SECOND abonnement, sans essai, donc un debit immediat.
//
// 60 s et non 30 · un cold start Render depasse regulierement la demi-minute,
// et abandonner pile dans cette fenetre est exactement le geste a eviter. Le
// backend est desormais idempotent sur cet endpoint, ce timeout n'est donc
// plus une garde de securite mais un simple filet d'ergonomie.
//
// LE TRANSFERT DES OCTETS N'EST PAS CONCERNE · l'upload video passe par
// `multipartUpload.js` (XMLHttpRequest et `fetch`), jamais par cette instance.
// Voir le commentaire de son propre timeout, ligne 128.
const api = axios.create({
  baseURL: API_URL,
  timeout: 60_000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor — auth token + injection automatique de season
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('insightball_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Injection auto de ?season= sur les endpoints saisonnalisés (GET uniquement)
    if (config.method === 'get' && shouldAddSeason(config.url)) {
      const season = getSelectedSeason()
      config.params = config.params || {}
      // Ne pas écraser si l'appelant a explicitement passé season
      if (config.params.season === undefined) {
        config.params.season = season
      }
    }

    return config
  },
  (error) => Promise.reject(error)
)

// Messages de secours par code HTTP — utilisés quand le backend ne renvoie
// pas de `detail` exploitable.
const STATUS_FALLBACKS = {
  401: 'Session expirée, reconnecte-toi.',
  403: 'Accès refusé.',
  404: 'Ressource introuvable.',
  429: 'Trop de tentatives, attends quelques instants.',
}

// Details backend qui ne doivent JAMAIS atteindre l'utilisateur tels quels.
//
// POURQUOI CETTE TABLE PLUTOT QU'UN REMPLACEMENT PAR CODE HTTP. L'immense
// majorite des `detail` de ce backend sont ecrits en francais ET destines a
// l'utilisateur — « Match introuvable », « Accès refusé », « Trop de
// requêtes », « Le mot de passe doit contenir au moins 8 caractères ». Les
// ignorer en bloc sur un code donne detruirait des messages utiles et
// remplacerait un texte precis par un texte vague.
//
// On ne neutralise donc QUE les chaines effectivement techniques, une par une,
// en les traduisant par leur equivalent metier. Tout le reste passe intact.
//
// Cas d'origine · un prospect a lu « Could not validate credentials » en plein
// wizard d'upload. Le repli `STATUS_FALLBACKS[401]` existait pourtant, en
// francais — mais il n'etait jamais atteint, le `detail` etant prioritaire et
// non vide. Un repli qu'on n'atteint jamais est un repli mort.
//
// AJOUTER ICI toute nouvelle chaine technique du backend. La bonne correction
// reste cote serveur, mais cette table est le filet.
const TECHNICAL_DETAILS = new Map([
  ['could not validate credentials', 'Session expirée, reconnecte-toi.'],
  ['not authenticated',              'Session expirée, reconnecte-toi.'],
  ['incorrect email or password',    'Email ou mot de passe incorrect.'],
  ['inactive user',                  'Ce compte est désactivé, contacte le support.'],
  ['token de download expire',       'Ce lien de téléchargement a expiré.'],
  ['token de download invalide ou deja consomme', "Ce lien de téléchargement n'est plus valide."],
  ['token non valide pour ce moment', "Ce lien n'est plus valide."],
])

// Traduit un detail technique, ou rend null si le detail est deja affichable.
function translateTechnical(detail) {
  const key = detail.trim().toLowerCase()
  if (TECHNICAL_DETAILS.has(key)) return TECHNICAL_DETAILS.get(key)

  // `ACCOUNT_DELETED:<recovery_token>` — codes machine que le backend renvoie
  // pour que Login.jsx branche dessus. Login les lit sur
  // `error.response.data.detail`, PAS sur `error.message` : son comportement
  // est donc intact. Mais tout autre consommateur affichant `err.message`
  // aurait montre le code brut — et, pour ACCOUNT_DELETED, un JETON DE
  // RECUPERATION en clair dans l'interface.
  if (key.startsWith('account_deleted')) {
    return 'Ce compte a été désactivé. Vérifie tes emails ou contacte le support.'
  }
  if (key === 'account_permanently_deleted') {
    return 'Ce compte a été définitivement supprimé.'
  }

  return null
}

// BUG-011 — extrait un message lisible de l'erreur axios.
// Priorité : detail string (traduit s'il est technique) → detail[0].msg
// (validation Pydantic) → detail.message (erreur métier objet) → fallback par
// status HTTP.
function friendlyMessage(error) {
  const detail = error.response?.data?.detail

  if (typeof detail === 'string' && detail.trim()) {
    return translateTechnical(detail) ?? detail
  }
  if (Array.isArray(detail) && typeof detail[0]?.msg === 'string') {
    return detail[0].msg
  }
  if (detail && typeof detail === 'object' && typeof detail.message === 'string') {
    return detail.message
  }

  // Timeout client · axios ne pose pas de `response`, le cas tomberait sinon
  // dans « Connexion impossible » — un message faux, qui accuse le reseau de
  // l'utilisateur alors que sa requete est probablement encore en cours cote
  // serveur. La nuance compte ici · c'est exactement la situation ou il ne
  // faut PAS reessayer a l'aveugle.
  if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
    return "Le serveur met trop de temps à répondre. Ton action a peut-être abouti — recharge la page avant de réessayer."
  }

  if (!error.response) {
    return 'Connexion impossible, vérifie ta connexion.'
  }

  const status = error.response.status
  if (STATUS_FALLBACKS[status]) {
    return STATUS_FALLBACKS[status]
  }
  if (status >= 500) {
    return 'Erreur serveur, on regarde ça.'
  }
  return 'Une erreur est survenue.'
}

// Response interceptor — redirection 401 + extraction du message lisible
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Eviter la boucle infinie si on est deja sur /login ou pages publiques
      const currentPath = window.location.pathname
      const publicPaths = ['/login', '/signup', '/', '/reset-password', '/recover', '/tarifs', '/a-propos']
      // `/football/*` retire le 08/08/2026 · ces URLs rendent un 410 servi par
      // Netlify, l'application ne les monte plus. La garde n'avait plus d'objet.
      const isPublic = publicPaths.includes(currentPath) || currentPath.startsWith('/club-invite/')

      if (!isPublic) {
        localStorage.removeItem('insightball_token')
        localStorage.removeItem('insightball_user')
        localStorage.removeItem('insightball_season')
        localStorage.removeItem('last_known_effective_season')
        window.location.href = '/login'
      }
    }

    // BUG-011 / ARCHI-001 — message lisible pour les composants, qui
    // affichent err.message tel quel. On enrichit l'erreur sans la swallow.
    error.message = friendlyMessage(error)
    return Promise.reject(error)
  }
)

export default api
